import { useState } from "react";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/lib/services";

export default function Contact() {
  const { t } = useContent();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-ink-800 text-bone-50 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: `url(${ASSETS.ladyJustice})`, backgroundSize: "cover", backgroundPosition: "right" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-800 via-ink-800/90 to-ink-800/40" />
        <div className="relative section pt-24 pb-20 md:pt-32 md:pb-28">
          <div className="eyebrow !text-gold-400 mb-6">{t("contact.eyebrow")}</div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-3xl">
            {t("contact.title")}
          </h1>
          <p className="mt-8 text-xl text-bone-100/80 leading-relaxed max-w-2xl font-light">
            {t("contact.subtitle")}
          </p>
        </div>
      </section>

      <section className="section py-20 md:py-28 grid md:grid-cols-12 gap-14">
        <aside className="md:col-span-4">
          <div className="border-t border-gold-500 pt-8 space-y-10">
            <div>
              <div className="eyebrow mb-4">{t("contact.address.label")}</div>
              <p className="font-serif text-xl text-ink-800 leading-snug" data-testid="contact-address">
                {t("contact.address")}
              </p>
            </div>
            <div>
              <div className="eyebrow mb-4">{t("contact.email.label")}</div>
              <a
                href={`mailto:${t("contact.email")}`}
                className="text-ink-800 hover:text-gold-600 text-lg"
                data-testid="contact-email"
              >
                {t("contact.email")}
              </a>
            </div>
            <div>
              <div className="eyebrow mb-4">{t("contact.phone.label")}</div>
              <a
                href={`tel:${t("contact.phone")}`}
                className="text-ink-800 hover:text-gold-600 text-lg"
                data-testid="contact-phone"
              >
                {t("contact.phone")}
              </a>
            </div>
          </div>
        </aside>

        <div className="md:col-span-8">
          {success ? (
            <div className="border border-gold-500 p-12 text-center" data-testid="contact-success">
              <i className="fa-solid fa-circle-check text-5xl text-gold-600" />
              <h3 className="mt-6 font-serif text-3xl text-ink-800">Thank you</h3>
              <p className="mt-4 text-ink-800/75 max-w-md mx-auto leading-relaxed">
                {t("contact.form.success")}
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="btn-outline-ink mt-8"
                data-testid="contact-send-another"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-7" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-7">
                <div>
                  <label className="eyebrow block mb-3">{t("contact.form.name")}</label>
                  <input
                    required
                    className="input-base"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">{t("contact.form.email")}</label>
                  <input
                    required
                    className="input-base"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-3">{t("contact.form.subject")}</label>
                <input
                  className="input-base"
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="eyebrow block mb-3">{t("contact.form.message")}</label>
                <textarea
                  required
                  rows={6}
                  className="input-base resize-none"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3" data-testid="contact-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-gold disabled:opacity-60"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : t("contact.form.submit")}{" "}
                {!submitting && <i className="fa-solid fa-arrow-right text-[10px]" />}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
